import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { gql, useLazyQuery, useQuery } from '@apollo/client';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import {
  type AutocompleteProps,
  type AutocompleteRenderGetTagProps,
  type AutocompleteValue,
  Chip,
  type InputProps,
  ListItem,
  ListItemAvatar,
  ListItemText,
  type SxProps,
  type Theme,
  debounce,
} from '@mui/material';
import { grey } from '@mui/material/colors';

import { useLocale } from '../../../src/hooks/locale';
import type {
  GetDefaultTeamsQuery,
  GetDefaultTeamsQueryVariables,
  TeamsSelectFragment,
  TeamsSelectQuery,
} from '../../__generated__/graphql';
import { RaAutoComplete } from '../data-grid/RaAutoComplete';
import { TeamAvatar } from '../TeamAvatar';

const TEAMS_SELECT_FRAGMENT = gql`
  fragment TeamsSelect on teams {
    id
    name
    logo {
      id
      url
    }
    team_images {
      image {
        url
      }
    }
  }
`;

const SEARCH_TEAMS = gql`
  ${TEAMS_SELECT_FRAGMENT}
  query TeamsSelect($search: String!, $excludeTeamsIds: [uuid!]!) {
    teams(
      where: { name: { _ilike: $search }, id: { _nin: $excludeTeamsIds } }
      limit: 20
    ) {
      id
      ...TeamsSelect
    }
  }
`;

const GET_DEFAULT_TEAMS = gql`
  ${TEAMS_SELECT_FRAGMENT}
  query GetDefaultTeams($ids: [uuid!]!) {
    teams(where: { id: { _in: $ids } }) {
      id
      ...TeamsSelect
    }
  }
`;

interface TeamsSelectProps<Multiple extends boolean = false>
  extends Omit<
    AutocompleteProps<TeamsSelectFragment, Multiple, false, false>,
    'renderInput' | 'options' | 'onChange' | 'value'
  > {
  teamIds?: AutocompleteValue<string, Multiple, false, false>;
  label?: string;
  onChange?: (
    teamId: AutocompleteValue<string, Multiple, false, false>,
  ) => void;
  size?: 'small' | 'medium';
  excludeTeamsIds?: string[];
  sx?: SxProps<Theme>;
  InputProps?: InputProps;
  autoFocus?: boolean;
  disabled?: boolean;
  loading?: boolean;
  openOnFocus?: boolean;
  fullWidth?: boolean;
  blurOnSelect?: boolean;
  clearOnBlur?: boolean;
}

export const TeamsSelect = <Multiple extends boolean = false>({
  onChange,
  InputProps,
  teamIds,
  excludeTeamsIds = [],
  autoFocus = false,
  sx,
  disabled = false,
  loading = false,
  openOnFocus = false,
  fullWidth = false,
  blurOnSelect = false,
  clearOnBlur = false,
  multiple,
}: TeamsSelectProps<Multiple>) => {
  const { t } = useLocale();
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = useState<TeamsSelectFragment[]>([]);
  const autocompleteRef = useRef<HTMLDivElement | null>(null);

  const [selectedValue, setSelectedValue] = useState<
    AutocompleteValue<
      TeamsSelectFragment & { isRestricted?: true },
      Multiple,
      false,
      false
    >
  >(
    (multiple ? [] : null) as AutocompleteValue<
      TeamsSelectFragment,
      Multiple,
      false,
      false
    >,
  );

  const { data: defaultTeams } = useQuery<
    GetDefaultTeamsQuery,
    GetDefaultTeamsQueryVariables
  >(GET_DEFAULT_TEAMS, {
    skip: !teamIds,
    variables: {
      ids: Array.isArray(teamIds) ? teamIds : teamIds == null ? [] : [teamIds],
    },
  });

  useEffect(() => {
    if (defaultTeams != null) {
      if (multiple) {
        setSelectedValue(
          (teamIds as string[]).map(tId => {
            const team = defaultTeams.teams?.find(t => t.id === tId);

            return team == null
              ? {
                  id: tId,
                  name: t('Restricted team'),
                  isRestricted: true,
                  logo: null,
                  team_images: [],
                }
              : team;
          }) as AutocompleteValue<
            TeamsSelectFragment & { isRestricted?: true },
            Multiple,
            false,
            false
          >,
        );
      } else {
        const selectedTeam = defaultTeams.teams?.[0];

        setSelectedValue(
          (selectedTeam == null && teamIds != null
            ? {
                id: teamIds as string,
                name: t('Restricted team'),
                isRestricted: true,
                logo: null,
                team_images: [],
              }
            : selectedTeam) as AutocompleteValue<
            TeamsSelectFragment & { isRestricted?: true },
            Multiple,
            false,
            false
          >,
        );
      }

      if (autoFocus && autocompleteRef.current != null) {
        autocompleteRef.current.querySelector('input')?.focus();
      }
    }
  }, [defaultTeams, autoFocus, multiple, t, teamIds]);

  const [searchTeams, { loading: loadingTeams, error }] =
    useLazyQuery<TeamsSelectQuery>(SEARCH_TEAMS, {
      variables: {
        excludeTeamsIds,
      },
    });

  const search = useCallback(
    (value: string) => {
      searchTeams({
        variables: {
          search: `%${value ?? ''}%`,
        },
      }).then(response => {
        setOpen(true);
        setOptions(response.data?.teams ?? []);
      });
    },
    [searchTeams],
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        search(value);
      }, 275),
    [search],
  );

  const isRestricted = useMemo(
    () =>
      !multiple &&
      (
        selectedValue as AutocompleteValue<
          TeamsSelectFragment & { isRestricted?: true },
          false,
          false,
          false
        >
      )?.isRestricted,
    [multiple, selectedValue],
  );

  return (
    <RaAutoComplete
      sx={sx}
      disabled={disabled}
      multiple={multiple}
      autoComplete={false}
      autoCorrect="off"
      open={open}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onClose={() => {
        setOpen(false);
        setOptions([]);
      }}
      onOpen={() => {
        search('');
      }}
      fullWidth={fullWidth}
      debouncedSearch={debouncedSearch}
      setOpen={setOpen}
      setOptions={setOptions}
      openOnFocus={openOnFocus}
      onFocus={() => openOnFocus && setOpen(true)}
      clearOnBlur={clearOnBlur}
      blurOnSelect={blurOnSelect}
      value={selectedValue}
      getOptionLabel={option => option.name ?? ''}
      options={options}
      loading={loadingTeams || loading}
      loadingText={t('Loading...')}
      noOptionsText={error ? t('Error') : t('No results')}
      InputProps={{
        ...InputProps,
        startAdornment: InputProps?.startAdornment ?? (
          <PeopleOutlinedIcon style={{ color: grey[700] }} />
        ),
        placeholder: disabled
          ? t('No team')
          : InputProps?.placeholder ?? t('Search teams'),
      }}
      onChange={async (
        _: any,
        value: AutocompleteValue<TeamsSelectFragment, Multiple, false, false>,
      ) => {
        setSelectedValue(
          value as AutocompleteValue<
            TeamsSelectFragment,
            Multiple,
            false,
            false
          >,
        );
        if (Array.isArray(value)) {
          onChange?.(
            value.map(team => team.id) as AutocompleteValue<
              string,
              Multiple,
              false,
              false
            >,
          );
        } else {
          onChange?.(
            (value?.id ?? null) as AutocompleteValue<
              string,
              Multiple,
              false,
              false
            >,
          );
        }
      }}
      filterOptions={x => x}
      renderOption={(props, option) => {
        return (
          <ListItem {...props} key={option.id}>
            <ListItemAvatar>
              <TeamAvatar
                logoUrl={
                  option.logo?.url ?? option.team_images?.[0]?.image?.url
                }
                size={40}
              />
            </ListItemAvatar>
            <ListItemText primary={option.name} />
          </ListItem>
        );
      }}
      renderTags={
        multiple
          ? (
              value: (TeamsSelectFragment & { isRestricted?: true })[],
              getTagProps: AutocompleteRenderGetTagProps,
            ) =>
              value
                .map((option, index) => {
                  const {
                    key,
                    disabled: chipDisabled,
                    ...tagProps
                  } = getTagProps({ index });

                  return (
                    <Chip
                      sx={{ '& .MuiAvatar-root': { ml: 0.5, mr: -0.5 } }}
                      key={key}
                      size="small"
                      label={option.name}
                      {...tagProps}
                      disabled={chipDisabled || option.isRestricted}
                    />
                  );
                })
                .filter(Boolean)
          : undefined
      }
      componentsProps={{
        paper: {
          elevation: 3,
          sx: {
            borderRadius: '4px',
          },
        },
      }}
      tooltip={
        isRestricted ? t("You don't have access to this team") : undefined
      }
    />
  );
};
